import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsUserSignedIn } from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { useProjectsListActions } from "../../core_stores/useProjectsListStore";
import { useSubscriptionActions } from "../../core_stores/useSubscriptionStore";
import { colors, effects } from "../../design/tokens";
import { usePromptInputActions } from "../../view_stores/usePromptInputStore";
import {
  SideMenuBehavior,
  useArePersonalSettingsOpen,
  useIsSideMenuOpen,
  useSideMenuActions,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import PersonalSettings from "../personal_settings/PersonalSettings";
import SideMenuBottomNav from "./components/SideMenuBottomNav";
import SideMenuHeader from "./components/SideMenuHeader";
import SideMenuProjects from "./components/SideMenuProjects";
import SideMenuSearchField from "./components/SideMenuSearchField";
import SideMenuSortDropdown from "./components/SideMenuSortDropdown";
import SideMenuStartNewProjectBtn from "./components/SideMenuStartNewProjectBtn";

export default function SideMenu(): JSX.Element {
  const sideMenuBehavior = useSideMenuBehavior();
  const isSideMenuOpen = useIsSideMenuOpen();
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();
  const isCompactView = useIsCompactView();
  const isUserSignedIn = useIsUserSignedIn();
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    close: closeSideMenu,
    setSelectedProjectId,
    switchBehavior,
    allowClose,
    scrollProjectsToTop,
  } = useSideMenuActions();
  const { loadProjects, clear: clearProjectsListStore } =
    useProjectsListActions();
  const { clear: clearPromptInput } = usePromptInputActions();
  const { reloadSubscriptionsInfo } = useSubscriptionActions();

  const isProjectPage = location.pathname.startsWith("/project/");

  useEffect(() => {
    if (!isCompactView) return;

    const shouldLockBody = isSideMenuOpen || arePersonalSettingsOpen;
    document.body.style.overflow = shouldLockBody ? "hidden" : "";
    document.body.style.height = shouldLockBody ? "100vh" : "";

    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isCompactView, isSideMenuOpen, arePersonalSettingsOpen]);

  useEffect(() => {
    if (isUserSignedIn) {
      loadProjects();
      reloadSubscriptionsInfo();
      scrollProjectsToTop();
    } else {
      clearProjectsListStore();
      setSelectedProjectId(null);
    }
  }, [isUserSignedIn]);

  useEffect(() => {
    if (isUserSignedIn && isSideMenuOpen) {
      reloadSubscriptionsInfo();
    }
  }, [isSideMenuOpen, isUserSignedIn]);

  useEffect(() => {
    function handleMouseLeave(event: MouseEvent): void {
      const isMouseLeavingLeft = event.clientX < 0;
      const isAutoMode = sideMenuBehavior === SideMenuBehavior.AUTO;
      if (isMouseLeavingLeft && isAutoMode && isSideMenuOpen && !arePersonalSettingsOpen) {
        closeSideMenu();
      }
    }

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => document.removeEventListener("mouseleave", handleMouseLeave);
  }, [sideMenuBehavior, isSideMenuOpen, arePersonalSettingsOpen, closeSideMenu]);

  function handleLogoClick(): void {
    if (!isProjectPage) return;

    window.dispatchEvent(new Event("darvin-block-reopen-menu"));
    closeSideMenu();
    clearPromptInput();
    navigate("/");
  }

  function handleCollapseClick(): void {
    if (sideMenuBehavior === SideMenuBehavior.PINNED) {
      switchBehavior();
    }
    window.dispatchEvent(new Event("darvin-block-reopen-menu"));
    closeSideMenu();
  }

  const isPinned = sideMenuBehavior === SideMenuBehavior.PINNED;
  const shouldBeShown = isUserSignedIn && (isPinned || isSideMenuOpen);
  const widthClass = isCompactView ? "w-full" : "w-80";
  const translateClass = shouldBeShown ? "translate-x-0" : "-translate-x-[110%]";

  return (
    <>
      <SideMenuBackdrop />

      <aside
        ref={menuRef}
        onMouseEnter={allowClose}
        className={`${widthClass} ${translateClass} h-full flex flex-col fixed inset-y-0 left-0
          ${colors.surface.primary} border-r ${colors.border.primary}
          transform ${effects.transition.default} ease-in-out z-40`}
      >
        <SideMenuHeader
          onCollapse={handleCollapseClick}
          onLogoClick={handleLogoClick}
        />

        <div className="flex-1 min-h-0 flex flex-col px-4 gap-4 overflow-hidden">
          <SideMenuStartNewProjectBtn />
          <SideMenuSearchField />
          <SideMenuSortDropdown />
          <SideMenuProjects />
        </div>

        <SideMenuBottomNav />
      </aside>

      <PersonalSettings />
    </>
  );
}

function SideMenuBackdrop(): JSX.Element {
  const { close: closeSideMenu, allowClose } = useSideMenuActions();
  const sideMenuBehavior = useSideMenuBehavior();
  const isSideMenuOpen = useIsSideMenuOpen();
  const arePersonalSettingsOpen = useArePersonalSettingsOpen();

  const isAutoMode = sideMenuBehavior === SideMenuBehavior.AUTO;
  const isPinnedMode = sideMenuBehavior === SideMenuBehavior.PINNED;

  function handleMouseEnter(): void {
    if (isAutoMode && !arePersonalSettingsOpen) {
      closeSideMenu();
    }
  }

  function handleClick(): void {
    allowClose();
    closeSideMenu();
  }

  const shouldShowBackdrop =
    (isAutoMode && isSideMenuOpen) || (isPinnedMode && arePersonalSettingsOpen);

  const visibilityClass = shouldShowBackdrop
    ? "opacity-100 pointer-events-auto"
    : "opacity-0 pointer-events-none";

  const blurClass = arePersonalSettingsOpen ? `${colors.overlay.default} backdrop-blur-sm` : "";

  return (
    <div
      className={`fixed inset-0 w-full h-full z-30 ${effects.transition.default} ease-in-out ${visibilityClass} ${blurClass}`}
      onMouseEnter={handleMouseEnter}
      onClick={handleClick}
    />
  );
}
